import { useState, useCallback } from "react"
import { CheckCircle, XCircle, Loader2, Tag } from "lucide-react"
import { Input } from "@/modules/shared/ui/components/input"
import { Button } from "@/modules/shared/ui/components/button"
import type { CouponValidationDto } from "../../dtos/coupon.dto"
import { couponSummary } from "../../hooks/use-coupon"

interface CouponInputProps {
  /** Called when a valid coupon is applied (or null when cleared). */
  onCouponChange: (coupon: CouponValidationDto | null) => void
  /** Validates the coupon code. Should call the API. */
  onValidate: (code: string) => Promise<CouponValidationDto>
  disabled?: boolean
}

export default function CouponInput({
  onCouponChange,
  onValidate,
  disabled = false,
}: CouponInputProps) {
  const [code, setCode] = useState("")
  const [isValidating, setIsValidating] = useState(false)
  const [validation, setValidation] = useState<CouponValidationDto | null>(
    null,
  )
  const [error, setError] = useState<string | null>(null)

  const handleApply = useCallback(async () => {
    const trimmed = code.trim()
    if (!trimmed) return

    setIsValidating(true)
    setError(null)
    setValidation(null)

    try {
      const result = await onValidate(trimmed)
      setValidation(result)

      if (result.isValid) {
        onCouponChange(result)
      } else {
        setError(result.error || "Invalid coupon code.")
        onCouponChange(null)
      }
    } catch {
      setError("Failed to validate coupon. Please try again.")
      onCouponChange(null)
    } finally {
      setIsValidating(false)
    }
  }, [code, onValidate, onCouponChange])

  const handleClear = useCallback(() => {
    setCode("")
    setValidation(null)
    setError(null)
    onCouponChange(null)
  }, [onCouponChange])

  const summary = couponSummary(validation ?? undefined)

  return (
    <div className="space-y-2">
      <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
        <Tag size={14} />
        Coupon Code (Optional)
      </label>

      <div className="flex gap-2">
        <Input
          placeholder="Enter coupon code"
          value={code}
          onChange={(e) => {
            setCode(e.target.value.toUpperCase())
            // Clear previous validation on change
            if (validation || error) {
              setValidation(null)
              setError(null)
              onCouponChange(null)
            }
          }}
          disabled={disabled || isValidating || (validation?.isValid ?? false)}
          className="flex-1"
        />

        {validation?.isValid ? (
          <Button
            type="button"
            variant="outline"
            onClick={handleClear}
            disabled={disabled}
            className="px-4"
          >
            Clear
          </Button>
        ) : (
          <Button
            type="button"
            onClick={handleApply}
            disabled={disabled || isValidating || !code.trim()}
              className="px-4 text-primary-foreground"
          >
            {isValidating ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              "Apply"
            )}
          </Button>
        )}
      </div>

      {/* Success message */}
      {validation?.isValid && summary && (
        <div className="flex items-start gap-2 text-sm text-green-700 bg-green-50 border border-green-200 rounded-md p-3">
          <CheckCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <div>
            <p className="font-medium">Coupon applied!</p>
            <p className="text-green-600">{summary}</p>
          </div>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="flex items-start gap-2 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md p-3">
          <XCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}
    </div>
  )
}
